import { useEffect, useState } from 'react'
import CustomerCombobox from '../CustomerCombobox'
import { searchLinkableJobs, type LinkableJob } from '../../lib/jobSearch'
import { BOXTOP_SYNC_ENABLED } from '../../lib/boxtopSync'

const overlay = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
const panel = 'w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 p-5 shadow-xl max-h-[90vh] overflow-y-auto'
const inputCls = 'w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white'
const btnPrimary = 'px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
const btnGhost = 'px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'

function ErrorBox({ message }: { message: string | null }) {
  if (!message) return null
  return <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400">{message}</div>
}

// ---------------------------------------------------------------------------------------------
export function AddChildDialog({ master, nextRef, busy, error, onSubmit, onClose }: {
  master: { job_ref: string; transport_mode: string }
  nextRef: string
  busy: boolean
  error: string | null
  onSubmit: (v: { houseBlNo: string; importerId: string; consignorId: string; containerNo: string }) => void
  onClose: () => void
}) {
  const [houseBlNo, setHouseBlNo] = useState('')
  const [importerId, setImporterId] = useState('')
  const [consignorId, setConsignorId] = useState('')
  const [containerNo, setContainerNo] = useState('')
  const isAir = master.transport_mode === 'AIR'
  return (
    <div className={overlay}>
      <div className={panel}>
        <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">+ Add child job</h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Will be created as <span className="font-mono font-bold">{nextRef}</span> under {master.job_ref}. It inherits direction, mode, ports,
          dates, vessel/voyage (or flight) and carrier from the master; everything can be edited on the job afterwards. Goods are added on the job itself.
        </p>
        <ErrorBox message={error} />
        <div className="space-y-3">
          <label className="block text-sm text-gray-700 dark:text-gray-300">{isAir ? 'House AWB (HAWB)' : 'House BL'}
            <input className={inputCls} value={houseBlNo} onChange={e => setHouseBlNo(e.target.value)} />
          </label>
          <div className="text-sm text-gray-700 dark:text-gray-300">Consignor (defaults to the master's)
            <CustomerCombobox value={consignorId} onChange={setConsignorId} anyRole placeholder="— Same as master —" />
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">Importer (defaults to the master's)
            <CustomerCombobox value={importerId} onChange={setImporterId} anyRole placeholder="— Same as master —" />
          </div>
          <label className="block text-sm text-gray-700 dark:text-gray-300">Container no.
            <input className={inputCls} value={containerNo} onChange={e => setContainerNo(e.target.value)} placeholder="optional" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button className={btnPrimary} disabled={busy} onClick={() => onSubmit({ houseBlNo, importerId, consignorId, containerNo })}>
            {busy ? 'Creating…' : 'Create child job'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
export function LinkJobDialog({ master, busy, error, onLink, onClose }: {
  master: { id: string; job_ref: string; direction: string; transport_mode: string }
  busy: boolean
  error: string | null
  onLink: (job: LinkableJob) => void
  onClose: () => void
}) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<LinkableJob[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [picked, setPicked] = useState<LinkableJob | null>(null)

  useEffect(() => {
    if (term.trim().length < 2) { setResults([]); setSearchError(null); return }
    let cancelled = false
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const rows = await searchLinkableJobs({ term, masterId: master.id })
        if (!cancelled) { setResults(rows); setSearchError(null) }
      } catch (e) {
        if (!cancelled) { setResults([]); setSearchError(`Search failed: ${e instanceof Error ? e.message : String(e)}`) }
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [term, master.id])

  const mismatch = picked && (picked.direction !== master.direction || picked.transport_mode !== master.transport_mode)
    ? `Note: ${picked.job_ref} is ${picked.direction}/${picked.transport_mode} but the master is ${master.direction}/${master.transport_mode}.`
    : null

  return (
    <div className={overlay}>
      <div className={panel}>
        <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Link existing job</h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Search by job ref. The job keeps its own reference (e.g. KI26070 stays KI26070); it is not renumbered.
        </p>
        <ErrorBox message={error || searchError} />
        {!picked ? (
          <>
            <input className={inputCls} autoFocus placeholder="Type at least 2 characters of the job ref…" value={term} onChange={e => setTerm(e.target.value)} />
            <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 rounded border border-gray-200 dark:border-gray-700">
              {searching && <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>}
              {!searching && term.trim().length >= 2 && results.length === 0 && !searchError && (
                <div className="px-3 py-2 text-sm text-gray-500">No job matches “{term.trim()}”.</div>
              )}
              {results.map(r => (
                <button
                  key={r.id}
                  disabled={!!r.blockedReason}
                  onClick={() => setPicked(r)}
                  className="block w-full px-3 py-2 text-left text-sm enabled:hover:bg-blue-50 dark:enabled:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="font-mono font-bold text-gray-900 dark:text-white">{r.job_ref}</span>
                  <span className="ml-2 text-xs text-gray-500">{r.direction}/{r.transport_mode} · {r.status ?? '—'}{BOXTOP_SYNC_ENABLED && r.boxtrax_synced ? ' · synced to BoxTop' : ''}</span>
                  {r.blockedReason && <div className="text-xs text-red-600 dark:text-red-400">{r.blockedReason}</div>}
                </button>
              ))}
              {results.length >= 50 && <div className="px-3 py-2 text-xs text-gray-500">Showing the first 50 — type more of the ref to narrow down.</div>}
            </div>
          </>
        ) : (
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <div>Link <span className="font-mono font-bold">{picked.job_ref}</span> under <span className="font-mono font-bold">{master.job_ref}</span>?</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>{picked.job_ref} keeps its reference. It will appear in this tab alongside the numbered children.</li>
              <li>The master's totals will include this job's goods.</li>
              {BOXTOP_SYNC_ENABLED && picked.boxtrax_synced && <li className="text-amber-700 dark:text-amber-400">It is already synced to BoxTop. BoxTop has no update function, so the BoxTop record is not changed by linking.</li>}
            </ul>
            {mismatch && <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">{mismatch}</div>}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          {picked && <button className={btnGhost} onClick={() => setPicked(null)} disabled={busy}>Back</button>}
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          {picked && <button className={btnPrimary} disabled={busy} onClick={() => onLink(picked)}>{busy ? 'Linking…' : 'Link job'}</button>}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
export function UnlinkDialog({ master, child, busy, error, onConfirm, onClose }: {
  master: { job_ref: string }
  child: { job_ref: string; total_packs?: number; total_gross_weight?: number; total_volume?: number; boxtrax_synced: boolean; boxtrax_ref: string | null }
  busy: boolean
  error: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <div className={overlay}>
      <div className={panel}>
        <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Unlink {child.job_ref} from {master.job_ref}?</h3>
        <ErrorBox message={error} />
        <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700 dark:text-gray-300">
          <li><span className="font-mono font-bold">{child.job_ref}</span> becomes an independent job. Its reference does <b>not</b> change and nothing on it is deleted.</li>
          <li>
            {master.job_ref}'s totals will drop by this job's goods
            ({child.total_packs || 0} packs, {(child.total_gross_weight || 0).toFixed(2)} kg gross, {(child.total_volume || 0).toFixed(4)} m³).
          </li>
          {!BOXTOP_SYNC_ENABLED ? null : child.boxtrax_synced ? (
            <li className="text-amber-700 dark:text-amber-400">
              <b>{child.job_ref} has already been synced to BoxTop{child.boxtrax_ref ? ` (${child.boxtrax_ref})` : ''}.</b> BoxTop has no update function, so
              the BoxTop record stays exactly as it is — it will still show as a house job under {master.job_ref}'s BoxTop master. Only this system's link is removed;
              fix BoxTop separately if needed.
            </li>
          ) : (
            <li>It has not been synced to BoxTop, so nothing changes there.</li>
          )}
          <li>The unlink is recorded in the audit log.</li>
        </ul>
        <div className="mt-5 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" disabled={busy} onClick={onConfirm}>
            {busy ? 'Unlinking…' : 'Unlink job'}
          </button>
        </div>
      </div>
    </div>
  )
}
